import { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'

import {
    clearGatewayMarker,
    messengerLink,
    messengerName,
    notifyOpener,
} from '.'
import type { GatewayReturnTab } from '.'

/** How long a successful payment stays on screen before the tab closes itself. */
export const AUTO_CLOSE_MS = 3000

/** How long `window.close()` gets before we assume it was refused. */
const CLOSE_GRACE_MS = 400

const persianDigits = (value: string) =>
    value.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])

const TITLES: Record<string, string> = {
    success: 'پرداخت موفق',
    fail: 'پرداخت ناموفق',
}

/**
 * What a tab the bank has brought back shows instead of the app.
 *
 * This tab was opened from a mini app inside a messenger's web client (see
 * `modules/gatewayHandoff`). It has none of the frame's storage — so no session
 * — and the payer's business is back in the messenger anyway. So: the verdict,
 * and one button home.
 *
 * Home is the tab that opened this one. `window.close()` gets the payer there
 * when it is allowed (a tab opened by script may close itself); otherwise the
 * messenger's own mini-app link.
 */
export default function GatewayReturnScreen({ from, outcome }: GatewayReturnTab) {
    const name = messengerName(from)
    const link = messengerLink(from)
    const isSuccess = outcome.result === 'success'
    const [reported] = useState(() => notifyOpener(outcome))
    const [closing, setClosing] = useState(isSuccess && reported)
    const counted = useRef(false)

    useEffect(() => {
        if (counted.current) return
        counted.current = true
        posthog.capture('gateway_return_screen_shown', {
            result: outcome.result,
            gateway: outcome.gateway,
            amount: outcome.amount,
            miniapp_platform: from,
            opener_reached: reported,
        })
    }, [from, outcome, reported])

    const back = (method: 'tap' | 'auto') => {
        posthog.capture(
            'gateway_return_back',
            { method, miniapp_platform: from, opener_reached: reported },
            { transport: 'sendBeacon' }
        )
        clearGatewayMarker()
        window.close()
        window.setTimeout(() => {
            // Still here: the browser would not let this tab close.
            if (window.closed) return
            setClosing(false)
            if (method === 'tap') window.location.href = link ?? '/'
        }, CLOSE_GRACE_MS)
    }

    // Only a success closes on its own, and only once the mini app has the
    // verdict — it is what will show the payer the result from here on. A
    // failure stays up to be read.
    useEffect(() => {
        if (!closing) return
        const timer = window.setTimeout(() => back('auto'), AUTO_CLOSE_MS)
        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const title = TITLES[outcome.result] ?? 'نتیجهٔ پرداخت'

    return (
        <div
            dir="rtl"
            className="min-h-screen flex flex-col justify-center items-center gap-5 px-6 text-center bg-white"
        >
            <p
                className={`text-2xl font-bold ${
                    isSuccess
                        ? 'text-green-1'
                        : outcome.result === 'fail'
                        ? 'text-red-1'
                        : ''
                }`}
            >
                {title}
            </p>

            {outcome.amount != null && (
                <p className="text-base">
                    مبلغ: {outcome.amount.toLocaleString('fa-IR')} تومان
                </p>
            )}
            {outcome.code && (
                <p className="text-sm text-text-gray">
                    کد پیگیری:{' '}
                    {/* As sent: zero-padded, and a leading zero is part of it. */}
                    <span dir="ltr">{persianDigits(outcome.code)}</span>
                </p>
            )}
            {outcome.result !== 'success' && outcome.result !== 'fail' && (
                <p className="text-sm text-text-gray leading-7">
                    نتیجهٔ این پرداخت هنوز به ما نرسیده است. اگر مبلغ از حساب
                    شما کم شده، تا چند دقیقهٔ دیگر در صندوق دیده می‌شود.
                </p>
            )}

            <button
                type="button"
                onClick={() => back('tap')}
                className="bg-green-1 text-white text-base rounded-md py-3 w-full max-w-sm mt-2"
            >
                {name ? `بازگشت به ${name}` : 'بازگشت به همیان'}
            </button>

            <p className="text-xs text-text-gray leading-6 max-w-sm">
                {closing
                    ? `تا چند لحظهٔ دیگر به ${name ?? 'همیان'} برمی‌گردید…`
                    : name
                    ? `اگر این صفحه بسته نشد، به زبانهٔ ${name} در مرورگر برگردید.`
                    : 'اگر این صفحه بسته نشد، به زبانهٔ قبلی در مرورگر برگردید.'}
            </p>
        </div>
    )
}
